import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import storageService from '../services/storageService';
import authService from '../services/authService';
import { handleFirebaseError, logError } from '../utils/errorHandler';
import { validateFileUpload } from '../utils/validation';

export const useFileUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const uploadFile = useCallback(async (file, uploadType = 'passOn', passOnId = null) => {
    if (!file) throw new Error('No file provided');

    // Validate file
    const validation = validateFileUpload(file);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      // Auto sign-in if not authenticated
      let currentUser = user;
      if (!currentUser) {
        await authService.signInAnonymously();
        currentUser = authService.getCurrentUser();
      }
      
      if (!currentUser) {
        throw new Error('Unable to authenticate user for file upload');
      }

      let result;
      
      if (uploadType === 'profile') {
        result = await storageService.uploadProfileImage(
          file,
          currentUser.uid,
          (progressValue) => setProgress(progressValue)
        );
      } else if (uploadType === 'passOn') {
        if (!passOnId) throw new Error('PassOn ID required for pass-on uploads');
        result = await storageService.uploadPassOnImage(
          file,
          currentUser.uid,
          passOnId,
          (progressValue) => setProgress(progressValue)
        );
      } else {
        throw new Error('Invalid upload type');
      }

      return result;
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      logError(err, { 
        action: 'fileUpload', 
        uploadType, 
        fileName: file.name,
        fileSize: file.size,
        userId: user?.uid 
      });
      throw new Error(errorMessage);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [user]);

  const deleteFile = useCallback(async (filePath) => {
    if (!filePath) throw new Error('File path required');

    setError(null);

    try {
      // Auto sign-in if not authenticated
      let currentUser = user;
      if (!currentUser) {
        await authService.signInAnonymously();
        currentUser = authService.getCurrentUser();
      }
      
      if (!currentUser) {
        throw new Error('Unable to authenticate user for file deletion');
      }

      await storageService.deleteFile(filePath);
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      setError(errorMessage);
      logError(err, { 
        action: 'fileDelete', 
        filePath,
        userId: user?.uid 
      });
      throw new Error(errorMessage);
    }
  }, [user]);

  const getDownloadURL = useCallback(async (filePath) => {
    if (!filePath) throw new Error('File path required');

    try {
      return await storageService.getDownloadURL(filePath);
    } catch (err) {
      const errorMessage = handleFirebaseError(err);
      logError(err, { 
        action: 'getDownloadURL', 
        filePath 
      });
      throw new Error(errorMessage);
    }
  }, []);

  return {
    uploadFile,
    deleteFile,
    getDownloadURL,
    uploading,
    progress,
    error,
    clearError: () => setError(null)
  };
};
